import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { ethers } from 'ethers';

import {
  watchAccountChanges,
  watchNetworkChanges,
  loadBalances,
  loadTokenSymbols,
} from '../store/interactions';
import { setContracts } from '../store/reducers/tokens';
import { setConnection, setNetwork, setAccount } from '../store/reducers/provider';
import {
  getNetworkConfig,
  isSupportedChain,
  switchToSepolia,
  getNetworkLabel,
} from '../utils/network';

import Navigation from './Navigation';
import Swap from './Swap';
import Deposit from './Deposit';
import Withdraw from './Withdraw';
import Charts from './Charts';
import TokenComponent from './TokenComponents';
import ErrorBoundary from './ErrorBoundary';

function App() {
  const dispatch = useDispatch();
  const [bootstrapping, setBootstrapping] = useState(true);
  const [walletMissing, setWalletMissing] = useState(false);

  const chainId = useSelector((state) => state.provider.chainId);
  const contracts = useSelector((state) => state.tokens.contracts);

  const hydrateForChain = useCallback(
    async (nextChainId, nextProvider) => {
      const networkConfig = getNetworkConfig(nextChainId);
      if (!networkConfig) {
        return;
      }

      dispatch(
        setContracts({
          dappToken1Address: networkConfig.amm1.dappTokenAddress,
          usdToken1Address: networkConfig.amm1.usdTokenAddress,
          dappToken2Address: networkConfig.amm2.dappTokenAddress,
          usdToken2Address: networkConfig.amm2.usdTokenAddress,
          amm1Address: networkConfig.amm1.ammAddress,
          amm2Address: networkConfig.amm2.ammAddress,
          aggregatorAddress: networkConfig.aggregator.address,
        })
      );

      if (nextProvider) {
        await loadTokenSymbols(nextProvider, networkConfig, dispatch);
        await loadBalances(nextProvider, networkConfig, dispatch);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    let removeAccounts = () => {};
    let removeChain = () => {};

    const bootstrap = async () => {
      if (!window.ethereum) {
        setWalletMissing(true);
        setBootstrapping(false);
        return;
      }

      try {
        const browserProvider = new ethers.providers.Web3Provider(window.ethereum);
        dispatch(setConnection(browserProvider));

        const network = await browserProvider.getNetwork();
        dispatch(setNetwork(network.chainId));

        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          dispatch(setAccount(ethers.utils.getAddress(accounts[0])));
        }

        if (isSupportedChain(network.chainId)) {
          await hydrateForChain(network.chainId, browserProvider);
        }

        removeAccounts = watchAccountChanges(dispatch);
        removeChain = watchNetworkChanges(dispatch, async (nextChainId) => {
          const refreshed = new ethers.providers.Web3Provider(window.ethereum);
          dispatch(setConnection(refreshed));
          if (isSupportedChain(nextChainId)) {
            await hydrateForChain(nextChainId, refreshed);
          }
        });
      } catch (error) {
        console.error('Bootstrap error:', error);
      } finally {
        setBootstrapping(false);
      }
    };

    bootstrap();
    return () => {
      removeAccounts();
      removeChain();
    };
  }, [dispatch, hydrateForChain]);

  if (bootstrapping) {
    return (
      <div className="app-shell">
        <div className="loading-block">
          <div className="spinner spinner--accent" aria-hidden="true" />
          <p>Loading blockchain data…</p>
        </div>
      </div>
    );
  }

  if (walletMissing) {
    return (
      <div className="app-shell">
        <div className="card state-panel">
          <h2>Wallet required</h2>
          <p>Install MetaMask or another Web3 wallet to continue.</p>
          <a
            className="btn btn--primary"
            href="https://metamask.io/download/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', maxWidth: 280, margin: '0 auto' }}
          >
            Install MetaMask
          </a>
        </div>
      </div>
    );
  }

  const unsupported = chainId !== null && !isSupportedChain(chainId);

  return (
    <ErrorBoundary>
      <div className="app-shell">
        <HashRouter>
          <Navigation />
          <main className="app-main">
            {unsupported ? (
              <div className="card state-panel">
                <h2>Unsupported Network</h2>
                <p>Switch to Sepolia to use the public demo.</p>
                <p className="helper-text" style={{ marginBottom: 20 }}>
                  Connected: {getNetworkLabel(chainId)}
                </p>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={async () => {
                    try {
                      await switchToSepolia();
                    } catch (error) {
                      console.error('Switch to Sepolia failed', error);
                      window.alert(error.message || 'Failed to switch network');
                    }
                  }}
                >
                  Switch to Sepolia
                </button>
              </div>
            ) : (
              <Routes>
                <Route exact path="/" element={<Swap />} />
                <Route path="/deposit" element={<Deposit />} />
                <Route path="/withdraw" element={<Withdraw />} />
                <Route path="/charts" element={<Charts />} />
                <Route
                  path="/tokens"
                  element={
                    contracts?.dappToken1Address ? (
                      <TokenComponent tokenAddress={contracts.dappToken1Address} />
                    ) : (
                      <p className="helper-text">Token address not found for the connected network.</p>
                    )
                  }
                />
              </Routes>
            )}
          </main>
        </HashRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
